import React, { useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { addPageNumber } from '../common/addPageNumber';

interface Props {
    data: any;
    setPdfBlob: (blob: Blob) => void;
}

const EmployeeAttendanceWeeklyReport: React.FC<Props> = ({ data, setPdfBlob }) => {
    const todayDate = moment().format('DD-MM-YYYY');

    // Helper function to convert 24-hour time to 12-hour format
    const convertTo12HourFormat = (time: string | null | undefined): string => {
        if (!time || time === '-') return '-';
        
        // Handle time format like "14:14" or "14:14:00"
        const timeParts = time.split(':');
        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        
        return `${hours}:${minutes} ${ampm}`;
    };

    const generatePDF = () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        doc.setFontSize(14);
        doc.text('Employee Attendance Weekly', 14, 15);

        const newPageStartY = 20;

        let startY = 20;
        // Pages are added *before* a table rather than after, so the document never
        // ends on an empty page.
        let isFirstBlock = true;

        data?.sites?.forEach((site: any) => {

            // 👉 Get all unique dates
            const dateSet = new Set<string>();
            site.employee.forEach((emp: any) => {
                emp.date.forEach((d: any) => {
                    if (d.day) dateSet.add(d.day);
                });
            });

            const dates = Array.from(dateSet).sort((a: any, b: any) =>
                new Date(a.split('/').reverse().join('-')).getTime() -
                new Date(b.split('/').reverse().join('-')).getTime()
            );
            const chunkSize = 7;

            const dateChunks: string[][] = [];
            for (let i = 0; i < dates.length; i += chunkSize) {
                dateChunks.push(dates.slice(i, i + chunkSize));
            }

            dateChunks.forEach((chunkDates) => {

                if (!isFirstBlock) {
                    doc.addPage();
                    startY = newPageStartY;
                }
                isFirstBlock = false;

                // 🔷 Site Header — repeated per page so continuation pages stay labelled
                doc.setFontSize(11);
                doc.text(site.site, 14, startY);
                startY += 5;

                const columns = [
                    { header: 'Employee', dataKey: 'employeeName' },
                    { header: 'Position', dataKey: 'position' },
                    ...chunkDates.map((d: string) => ({
                        header: `${d}\nIN/OUT`,
                        dataKey: d
                    }))
                ];

                const rows = site.employee.map((emp: any) => {
                    const row: any = {
                        employeeName: emp.employeeName,
                        position: emp.position
                    };

                    const map: any = {};
                    emp.date.forEach((d: any) => {
                        map[d.day] = d;
                    });

                    chunkDates.forEach((date: string) => {
                        const entry = map[date];
                        row[date] = entry
                            ? `${convertTo12HourFormat(entry.in)} / ${convertTo12HourFormat(entry.out)}`
                            : '-';
                    });

                    return row;
                });

                autoTable(doc, {
                    head: [columns.map(col => col.header)],
                    body: rows.map((r: any) => columns.map(col => r[col.dataKey])),
                    startY: startY,
                    theme: 'grid',
                    styles: {
                        fontSize: 7,
                        cellPadding: 1,
                    }
                });

                startY = (doc as any).lastAutoTable.finalY + 10;
            });
        });

        // Footer
        addPageNumber(doc);

        const pdfBlob = doc.output('blob');
        setPdfBlob(pdfBlob);
    };

    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null;
};

export default EmployeeAttendanceWeeklyReport;